import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useBillingDialogs } from "@/contexts/BillingDialogs";
import { usePlanLimits } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  UploadCloud, Download, FileSpreadsheet, Sparkles, Trash2, Loader2,
  Database, Mail, CheckCircle2, ArrowLeft, Linkedin, ClipboardList, Coins,
} from "lucide-react";

// ── Status presentation (semantic, not default grey badges) ──────────────────
const STATUS = {
  enriched:      { label: "Enriched",      cls: "text-violet-text bg-primary/10 ring-primary/20",                 dot: "bg-primary" },
  cache_hit:     { label: "In your DB",    cls: "text-success bg-success/10 ring-success/20",                 dot: "bg-success" },
  not_found:     { label: "Not found",     cls: "text-muted-foreground bg-muted ring-border",                dot: "bg-muted-foreground/50" },
  invalid_input: { label: "Invalid input", cls: "text-warning bg-warning-soft ring-warning/20",          dot: "bg-warning" },
  no_credits:    { label: "No credits",    cls: "text-destructive bg-destructive/10 ring-destructive/20",    dot: "bg-destructive" },
};

const STATUS_LABEL = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.label]));

const STAGES = [
  { key: "read",     label: "Reading your rows" },
  { key: "match",    label: "Matching against your database" },
  { key: "verify",   label: "Checking emails" },
  { key: "finalize", label: "Finalizing results" },
];

const CSV_HEADERS = ["status", "name", "title", "domain", "company_name", "email", "email_status", "email_confidence", "industry", "size", "location", "linkedin"];
const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.not_found;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${s.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function StatTile({ icon: Icon, value, label, tone = "default" }) {
  const toneCls = {
    default: "text-foreground",
    primary: "text-violet-text",
    success: "text-success",
    muted: "text-muted-foreground",
  }[tone];
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3.5 shadow-card transition-colors hover:border-border/80" data-testid={`stat-${label}`}>
      <div className="eyebrow flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`mt-1 font-display text-3xl tracking-tight tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function Enrich() {
  const { user, refreshUser } = useAuth();
  const billing = useBillingDialogs();
  const limits = usePlanLimits(user?.plan);
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const fileRef = useRef(null);
  const timers = useRef([]);

  const loadJobs = async () => {
    if (!user?.id) return;
    const { data, error } = await supabase
      .from("enrichment_jobs")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    if (!error) setJobs(data || []);
  };

  useEffect(() => { loadJobs(); }, [user?.id]); // eslint-disable-line
  useEffect(() => () => timers.current.forEach(clearInterval), []);

  const parseInput = (raw) =>
    raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
      const parts = line.split(/[,\t]/).map((p) => p.trim()).filter(Boolean);
      if (parts.length === 1) {
        const v = parts[0];
        if (v.includes("@")) return { email: v, domain: v.split("@")[1] };
        if (/linkedin\.com\/in\//i.test(v)) return { linkedin_url: v };
        if (v.includes(".")) return { domain: v };
        return { name: v };
      }
      return { name: parts[0], domain: parts[1], email: parts[2] };
    });

  const rowCount = parseInput(text).length;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const t = await file.text();
    const lines = t.split(/\r?\n/);
    const head = (lines[0] || "").toLowerCase();
    const body = /name|domain|email/.test(head) ? lines.slice(1).join("\n") : t;
    setText(body);
    if (!label) setLabel(file.name.replace(/\.[^/.]+$/, ""));
  };

  const startProgressAnimation = () => {
    setProgress(6);
    setStageIdx(0);
    const p = setInterval(() => setProgress((v) => (v < 90 ? v + Math.max(1, (90 - v) * 0.08) : v)), 140);
    const s = setInterval(() => setStageIdx((i) => Math.min(i + 1, STAGES.length - 1)), 700);
    timers.current = [p, s];
  };
  const stopProgressAnimation = () => {
    timers.current.forEach(clearInterval);
    timers.current = [];
  };

  const run = async () => {
    if (!user?.id) return;
    const rows = parseInput(text);
    if (rows.length === 0) { toast.error("Add at least one row"); return; }
    // The server refuses oversized uploads too (migration 031); checking here
    // first saves the person a round trip and explains why.
    if (rows.length > limits.max_rows_per_job) {
      billing.showLimitReached({ reason: "rows", max: limits.max_rows_per_job, rows: rows.length });
      return;
    }
    if (user?.account_status === "frozen") { billing.showLimitReached({ reason: "frozen" }); return; }
    setRunning(true);
    setResult(null);
    startProgressAnimation();

    try {
      const jobLabel = label || "Untitled list";
      const { data: job, error: insertErr } = await supabase
        .from("enrichment_jobs")
        .insert({ user_id: user.id, label: jobLabel, input_count: rows.length, rows, status: "queued" })
        .select().single();
      if (insertErr) {
        const msg = insertErr.message || "";
        if (msg.startsWith("row_limit")) {
          billing.showLimitReached({ reason: "rows", max: Number(msg.split(":")[1]) || limits.max_rows_per_job, rows: rows.length });
        } else if (msg.includes("account_frozen")) {
          billing.showLimitReached({ reason: "frozen" });
        } else {
          toast.error(msg);
        }
        return;
      }

      // Catalog-backed when the search API is configured: matches against the
      // full 124.9M rather than the 72k that run_enrichment_job() could see.
      // Falls back to the Postgres RPC so nothing breaks without the API.
      let summary;
      if (process.env.REACT_APP_SEARCH_API_URL) {
        const { data: s } = await supabase.auth.getSession();
        const res = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/enrich-job`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${s?.session?.access_token}`,
            },
            body: JSON.stringify({ job_id: job.id }),
          },
        );
        summary = await res.json().catch(() => null);
        if (!res.ok || summary?.error) {
          toast.error(
            summary?.error === "match_failed"
              ? "Enrichment is temporarily unavailable — you were not charged."
              : "Could not run this job.",
          );
          return;
        }
      } else {
        const { data, error: runErr } = await supabase.rpc("run_enrichment_job", { p_job_id: job.id });
        if (runErr) { toast.error(runErr.message); return; }
        if (data?.error) { toast.error("Could not run this job."); return; }
        summary = data;
      }

      const { data: finished, error: fetchErr } = await supabase
        .from("enrichment_jobs").select("*").eq("id", job.id).single();
      if (fetchErr) { toast.error(fetchErr.message); return; }

      // Land the progress bar cleanly before revealing results.
      stopProgressAnimation();
      setStageIdx(STAGES.length - 1);
      setProgress(100);
      await new Promise((r) => setTimeout(r, 420));

      setResult(finished);
      setJobs((prev) => [finished, ...prev]);
      toast.success(
        `${finished.enriched_count} enriched · ${finished.cache_hit_count} from your DB · ${finished.not_found_count} not found`
      );
      if (finished.credits_spent > 0) refreshUser();
    } catch (e) {
      toast.error(e.message || "Enrichment failed");
    } finally {
      stopProgressAnimation();
      setRunning(false);
      setProgress(0);
      setStageIdx(0);
    }
  };

  const rowsForExport = (job) => (job.rows || []).map((r) => ({
    status: STATUS_LABEL[r.status] || r.status,
    name: r.name || r.input?.name || "",
    title: r.title || "",
    domain: r.domain || r.input?.domain || "",
    company_name: r.company_name || r.input?.company_name || "",
    email: r.email || "",
    email_status: r.email_status || "",
    email_confidence: r.email_confidence || "",
    industry: r.industry || "",
    size: r.size || "",
    location: r.location || "",
    linkedin: r.linkedin || "",
  }));

  const downloadCsv = (job) => {
    const rows = rowsForExport(job);
    const csv = [CSV_HEADERS.join(","), ...rows.map((r) => CSV_HEADERS.map((h) => csvEscape(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${job.label || "enriched"}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const deleteJob = async (id) => {
    const { error } = await supabase.from("enrichment_jobs").delete().eq("id", id).eq("user_id", user.id);
    if (error) { toast.error(error.message); return; }
    setJobs((prev) => prev.filter((j) => j.id !== id));
    if (result?.id === id) setResult(null);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <div>
          <div className="eyebrow">Workspace</div>
          <h1 className="font-display text-2xl tracking-tight">Enrich</h1>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-12">
        <div className="mx-auto grid w-full max-w-3xl gap-8">

          {/* ── PROCESSING ─────────────────────────────────────────────── */}
          {running && (
            <div className="animate-in fade-in zoom-in-95 duration-300 rounded-2xl border border-border bg-surface p-8">
              <div className="flex items-center gap-3">
                <span className="relative grid h-10 w-10 place-items-center rounded-full bg-primary/10">
                  <Sparkles className="h-5 w-5 text-violet-text" />
                </span>
                <div>
                  <div className="font-display text-xl tracking-tight">Enriching {rowCount} {rowCount === 1 ? "row" : "rows"}</div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" />
                    {STAGES[stageIdx].label}…
                  </div>
                </div>
                <div className="ml-auto font-display text-2xl tabular-nums text-muted-foreground">{Math.round(progress)}%</div>
              </div>

              <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="shimmer h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {STAGES.map((st, i) => (
                  <div key={st.key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors ${
                    i < stageIdx ? "border-success/30 bg-success/5 text-success"
                    : i === stageIdx ? "border-primary/40 bg-primary/5 text-foreground"
                    : "border-border text-muted-foreground/60"
                  }`}>
                    {i < stageIdx
                      ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      : i === stageIdx
                        ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                        : <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-current/40" />}
                    <span className="truncate">{st.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── INPUT ──────────────────────────────────────────────────── */}
          {!result && !running && (
            <>
              <div className="space-y-2">
                <h2 className="font-display text-3xl tracking-tight">Enrich a list</h2>
                <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
                  Drop a CSV or paste one row per line. We match against your own database first —
                  that's <span className="text-foreground">free</span>. Finding a new email costs
                  {" "}<span className="text-foreground">1 credit</span>, and only when we actually find one.
                </p>
              </div>

              <label
                data-testid="enrich-upload-zone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={async (e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) { fileRef.current.files = e.dataTransfer.files; await onFile({ target: fileRef.current }); }
                }}
                className="group flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface px-8 py-14 text-center transition-all hover:border-primary hover:bg-primary/[0.03]"
              >
                <span className="grid h-12 w-12 place-items-center rounded-full bg-accent text-accent-foreground transition-transform group-hover:scale-105">
                  <UploadCloud className="h-5 w-5 transition-colors group-hover:text-violet-text" />
                </span>
                <div className="mt-3 font-medium">Drop your CSV here</div>
                <div className="text-xs text-muted-foreground">or click to browse · max 10MB</div>
                <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={onFile} data-testid="enrich-file-input" />
              </label>

              <div className="grid gap-4">
                <div>
                  <Label htmlFor="label">List name</Label>
                  <Input id="label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Q1 Outbound — Fintech UK" className="mt-1.5" data-testid="enrich-label-input" />
                </div>
                <div>
                  <Label htmlFor="rows">Or paste rows</Label>
                  <Textarea
                    id="rows" rows={6} value={text} onChange={(e) => setText(e.target.value)}
                    data-testid="enrich-rows-input"
                    placeholder={`Acme Inc, acme.com\nstripe.com\nada@notion.so`}
                    className="mt-1.5 font-mono-bd text-sm"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ClipboardList className="h-3.5 w-3.5" />
                    {rowCount} row{rowCount === 1 ? "" : "s"} detected
                  </div>
                  <Button onClick={run} disabled={!text.trim()} size="lg" className="gap-2" data-testid="run-enrich-btn">
                    <Sparkles className="h-4 w-4" /> Enrich list
                  </Button>
                </div>
              </div>

              {jobs.length > 0 && (
                <div className="mt-4">
                  <div className="mb-3 eyebrow">Recent jobs</div>
                  <div className="overflow-hidden rounded-xl border border-border">
                    {jobs.map((j) => (
                      <div key={j.id} className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3 transition-colors last:border-b-0 hover:bg-surface-hover" data-testid={`enrich-job-${j.id}`}>
                        <button onClick={() => setResult(j)} className="flex flex-1 items-center gap-3 text-left">
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
                            <FileSpreadsheet className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium leading-tight">{j.label}</div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                              <span className="tabular-nums">{j.input_count} rows</span>
                              <span className="text-violet-text">· {j.enriched_count || 0} enriched</span>
                              <span className="text-success">· {j.cache_hit_count || 0} in DB</span>
                              <span>· {new Date(j.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </button>
                        <Button size="icon" variant="ghost" onClick={() => downloadCsv(j)} aria-label="Download"><Download className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => deleteJob(j.id)} aria-label="Delete"><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── RESULT ─────────────────────────────────────────────────── */}
          {result && !running && (
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => setResult(null)} aria-label="Back" data-testid="back-to-upload">
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <div className="eyebrow">Result</div>
                    <h2 className="font-display text-2xl tracking-tight">{result.label}</h2>
                  </div>
                </div>
                <Button onClick={() => downloadCsv(result)} className="gap-2" data-testid="download-csv-btn">
                  <Download className="h-4 w-4" /> Download CSV
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile icon={ClipboardList} label="Total" value={result.input_count} />
                <StatTile icon={Mail} label="Enriched" value={result.enriched_count || 0} tone="primary" />
                <StatTile icon={Database} label="In your DB" value={result.cache_hit_count || 0} tone="success" />
                <StatTile icon={Coins} label="Credits used" value={result.credits_spent || 0} />
              </div>

              <div className="overflow-hidden rounded-xl border border-border" data-testid="enrich-result-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-surface-inset text-left text-xs font-medium text-muted-foreground">
                        <th className="px-4 py-2.5 font-medium">Status</th>
                        <th className="px-3 py-2.5 font-medium">Name</th>
                        <th className="px-3 py-2.5 font-medium">Email</th>
                        <th className="px-3 py-2.5 font-medium">Industry</th>
                        <th className="px-3 py-2.5 font-medium">Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(result.rows || []).map((r, i) => {
                        const name = r.name || r.input?.name || "";
                        const domain = r.domain || r.input?.domain || "";
                        return (
                          <tr key={i} className="border-b border-border/60 transition-colors last:border-b-0 hover:bg-surface-hover">
                            <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2.5">
                                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent text-[11px] font-medium text-accent-foreground">
                                  {initials(name || domain)}
                                </span>
                                <div className="min-w-0">
                                  <div className="truncate font-medium">{name || "—"}</div>
                                  <div className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                                    {r.title ? <span className="truncate">{r.title}</span> : <span className="truncate">{domain || "—"}</span>}
                                    {r.linkedin && <a href={r.linkedin} target="_blank" rel="noreferrer" className="text-violet-text/70 hover:text-violet-text"><Linkedin className="h-3 w-3" /></a>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              {r.email
                                ? <span className="font-mono-bd text-xs">{r.email}</span>
                                : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground">{r.industry || "—"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{r.location || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
